// src/data/disciplineLetters.js
//
// THE DISCIPLINE CORRESPONDENCE SET. Ruling 180 (2026-09-17), taken under PD-19 and withdrawable by
// number. Master list 3.4, and 3.5 - ruling 158's outstanding second half.
//
// ---------------------------------------------------------------------------------------------
// WHAT RULING 18 ASKED FOR, IN BOB'S ORDER:
//
//   "Letters of demand for meetings, postponements, cancelling hearings, exhibits, witnesses,
//    pre-hearing meetings" - and "the LC uploads exhibits AND the carrier's case"
//   (data\YCP_LC_GC_WORKSPACE_REQUIREMENTS_2026-08-23.md SS5.3)
//
// Plus ruling 158 (2026-09-17): the preservation demand, a PREHEARING act due from the notice of
// charge, one per media class. The act was built that day; "the demand LETTER itself" was named as
// not built and left as the next item on the thread. This is it.
//
// ---------------------------------------------------------------------------------------------
// THE LETTERS ARE NOT A NEW VOCABULARY. THEY ARE THE ACTS THAT ALREADY CARRY A `doc`.
//
// src\data\hearingEvents.js already models every one of these as an ACT, and every act that produces
// a document already names it in its own `doc` field - "the demand letter", "the request", "the
// exhibit list", "the preservation demand". Inventing a second list of letter types beside that
// would be directives XI: the same fact in two places, with nothing keeping them in agreement. So
// this module generates the TEXT for an act, keyed on the act's own kind, and a letter type that is
// not an organization-side act with a `doc` does not exist here.
//
// ONE CONSEQUENCE, NAMED RATHER THAN QUIETLY RESOLVED. Ruling 18 lists "cancelling hearings" among
// the Organization's letters. In the model of record, `hearing_cancelled` is a CARRIER-side act -
// the carrier cancels its own hearing and the Organization receives the notice. So no cancellation
// letter is generated here. Either the model is wrong or the ruling's phrase meant "a letter about a
// cancellation", and that is Bob's to say; it is not resolved by writing a letter the model says the
// Organization does not send.
//
// ---------------------------------------------------------------------------------------------
// EVERY LETTER OBEYS THE SAME THREE RULES AS THE PRESENTMENT GROUP (src\data\letterGroup.js):
//
//   1. The header resolves from platform data, never typed. Three blocks: General Committee, Local
//      Committee, Railroad.
//   2. Anything that does not resolve prints [NOT ON FILE] and the letter is UNSENDABLE. Nothing is
//      guessed, filled or inferred (data\YCP_EVIDENTIARY_STANDARD_2026-08-23.md).
//   3. The body is built from the case record. No agreement language is quoted here at all, so
//      PD-16 is not engaged - and that is deliberate: a demand letter states what is demanded and
//      cites the case, and the moment it starts quoting rule text it needs QUO-1 behind every line.
//
// Pure module: no React, no Firestore.

import { resolveContactBlocks, presentmentLetterGroup, NOT_ON_FILE, letterHeader } from './letterGroup';
import { PRESERVATION_CLASSES } from './hearingEvents';
import { markDraft } from './signoff';

const F = Object.freeze;

// The organization-side acts that produce a letter. The label is the letter's own title; the act
// kind is the key, so a letter cannot exist for an act the timeline does not know.
export const LETTER_FOR_ACT = F({
  preservation_demanded:    F({ title: 'DEMAND FOR PRESERVATION OF EVIDENCE', perMediaClass: true,  ruling: 158 }),
  prehearing_meeting_demanded: F({ title: 'DEMAND FOR PRE-HEARING MEETING',   perMediaClass: false, ruling: 18 }),
  witnesses_demanded:       F({ title: 'DEMAND FOR PRODUCTION OF WITNESSES',  perMediaClass: false, ruling: 18 }),
  evidence_demanded:        F({ title: 'DEMAND FOR PRODUCTION OF EVIDENCE',   perMediaClass: false, ruling: 18 }),
  witness_list_served:      F({ title: 'WITNESS LIST',                        perMediaClass: false, ruling: 18 }),
  exhibits_served:          F({ title: 'EXHIBIT LIST',                        perMediaClass: false, ruling: 18 }),
  // `postponement_requested` carries side 'either' in hearingEvents.js, because EITHER party may ask
  // for a postponement - the carrier can request one too. The letter below is the ORGANIZATION'S
  // SIDE of that act, and its existence here is not a claim that only the Organization may ask.
  postponement_requested:   F({ title: 'REQUEST FOR POSTPONEMENT',            perMediaClass: false, ruling: 18 }),
  // RULINGS 186/189/191 (2026-09-19): the Organization's REQUEST that the carrier cancel its hearing.
  cancellation_requested:   F({ title: 'REQUEST TO CANCEL THE HEARING',       perMediaClass: false, ruling: 189 }),
});

// RULINGS 189/191 - the grounds, in Bob's order: the Organization's charge against the carrier's charges
// first, another reason next, and no reason LAST - "Best to offer a reason, to allow our quest, than to
// give them a reason to deny it." `sentence` is the plain statement of the request; nothing is argued
// on the member's behalf beyond what the officer chose (PD-4).
export const CANCELLATION_GROUNDS = F([
  F({ key: 'no_grounds', label: 'There are no grounds for the charges', sentence: 'The Organization submits that there are no grounds for the charges.' }),
  F({ key: 'meritless',  label: 'The charges are baseless or meritless', sentence: 'The Organization submits that the charges are baseless and without merit.' }),
  F({ key: 'other',      label: 'Another reason (written in)',          sentence: null }),
  F({ key: 'none',       label: 'No reason given',                      sentence: null, risk: true }),
]);

// RULING 191: the two risk warnings, the wording Bob approved as proposed. Shown to the officer BEFORE a
// letter is produced; never printed in the letter.
export const RISK_WARNINGS = F({
  noReason: 'A request with no reason gives the carrier an easy reason to deny it. Offer a reason if you have one.',
  postponement: 'A postponement request can be denied. Give a reasonable explanation, and attach proof where it is needed.',
});

// The warning that applies to a letter, or null. Pure; the panel shows it before generating.
export function riskWarningFor(kind, input) {
  const i = input || {};
  if (kind === 'postponement_requested') return RISK_WARNINGS.postponement;
  if (kind === 'cancellation_requested' && i.ground === 'none') return RISK_WARNINGS.noReason;
  return null;
}

export const LETTER_KINDS = F(Object.keys(LETTER_FOR_ACT));

// MASTER LIST 10.16 (2026-09-19): what the Letters panel asks for, per letter. `lines` fields take one entry per
// line; `choice` offers CANCELLATION_GROUNDS in Bob's order; `classes` offers the media classes (ruling 158).
// The panel draws these and hands the result to letterInputFromForm(); it writes no letter text itself.
export const LETTER_FIELDS = F({
  preservation_demanded:       F([F({ key: 'mediaClasses', label: 'Media to preserve', kind: 'classes' })]),
  prehearing_meeting_demanded: F([F({ key: 'purpose', label: 'Purpose of the meeting', kind: 'text' })]),
  witnesses_demanded:          F([F({ key: 'items', label: 'Witnesses demanded (one per line)', kind: 'lines' })]),
  evidence_demanded:           F([F({ key: 'items', label: 'Evidence demanded (one per line)', kind: 'lines' })]),
  witness_list_served:         F([F({ key: 'items', label: 'Witnesses (one per line)', kind: 'lines' })]),
  exhibits_served:             F([F({ key: 'items', label: 'Exhibits (one per line)', kind: 'lines' })]),
  postponement_requested:      F([F({ key: 'reason', label: 'Reason for the postponement', kind: 'text' }),
                                  F({ key: 'enclosures', label: 'Proof enclosed (one per line, optional)', kind: 'lines' })]),
  cancellation_requested:      F([F({ key: 'ground', label: 'Ground for the request', kind: 'choice' }),
                                  F({ key: 'reason', label: 'The other reason (only when "Another reason" is chosen)', kind: 'text' })]),
});

// A form (strings, booleans) becomes the letter's input: `lines` split on new lines and emptied lines dropped;
// `classes` kept only where they are real media classes; everything else trimmed. Pure.
export function letterInputFromForm(kind, form) {
  const f = form || {};
  const out = {};
  for (const fld of (LETTER_FIELDS[kind] || [])) {
    const v = f[fld.key];
    if (fld.kind === 'lines') out[fld.key] = String(v || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean);
    else if (fld.kind === 'classes') out[fld.key] = (Array.isArray(v) ? v : []).filter(c => PRESERVATION_CLASSES.includes(c));
    else out[fld.key] = typeof v === 'string' ? v.trim() : v;
  }
  return out;
}

// Ruling 18 named this and the model of record says the carrier does it. Exported so the gap is
// reportable rather than a silent absence from the list above.
export const NOT_GENERATED = F([
  F({ act: 'hearing_cancelled', why: 'The model of record makes cancellation a CARRIER-side act - the carrier cancels its own hearing and the Organization receives the notice. Rulings 186, 189 and 191 (2026-09-19) settle ruling 18’s "cancelling hearings": the Organization REQUESTS cancellation - see the act cancellation_requested and its letter. No letter is generated for the carrier’s own act.' }),
]);

const FOOT = '\n\nEvery statement above is taken from the discipline case record as filed on this platform; '
  + 'nothing is added, paraphrased or inferred (data\\YCP_EVIDENTIARY_STANDARD_2026-08-23.md). '
  + 'No agreement language is quoted in this letter.';

function nonEmpty(v) { return typeof v === 'string' ? v.trim() !== '' : v != null && v !== ''; }
function field(obj, key, label, missing) {
  const v = obj && obj[key];
  if (nonEmpty(v)) return String(v).trim();
  missing.push(label);
  return NOT_ON_FILE;
}

// A discipline case is not a claim, but the three header blocks resolve from the same office spine
// and the same carrier contact record. This adapts one to the other rather than duplicating
// officeBlock() - the alternative is the same resolution logic in two files (XI).
function asAddressable(kase) {
  const k = kase || {};
  return {
    id: k.caseNumber || k.id || null,
    committee: k.generalCommittee || k.committee || null,
    generalCommittee: k.generalCommittee || k.committee || null,
    scopeKey: k.scopeKey || null,
    railroad: k.railroad || null,
    filedAt: k.openedAt || k.createdAt || null,
  };
}

export function caseHeaderBlocks(kase, ctx) {
  return resolveContactBlocks(asAddressable(kase), ctx || {});
}

function caseIdentity(kase, missing) {
  const k = kase || {};
  return [
    'DISCIPLINE CASE ' + field(k, 'caseNumber', 'case number', missing),
    'Member: ' + field(k, 'memberName', 'member name', missing),
    'Railroad: ' + field(k, 'railroad', 'railroad', missing),
    'Notice of charge dated: ' + field(k, 'noticeDate', 'date of the notice of charge', missing),
  ].join('\n');
}

function bodyFor(kind, kase, input, missing) {
  const i = input || {};
  const k = kase || {};
  if (kind === 'preservation_demanded') {
    // Ruling 158: one demand per media class, and the class is what makes the demand specific.
    const cls = Array.isArray(i.mediaClasses) && i.mediaClasses.length
      ? i.mediaClasses.filter(c => PRESERVATION_CLASSES.includes(c))
      : [];
    if (!cls.length) missing.push('at least one media class (' + PRESERVATION_CLASSES.join(', ') + ')');
    return [
      'The Organization demands that the Carrier preserve, and not destroy, overwrite, recycle or',
      'otherwise dispose of, the following classes of recorded media bearing on this matter:',
      '',
      cls.length ? cls.map(c => '  - ' + c).join('\n') : '  ' + NOT_ON_FILE,
      '',
      'This demand is made at the prehearing stage, from the date of the notice of charge shown above.',
      'The Organization requests written confirmation that the material has been preserved. If any part',
      'of it has already been destroyed or is otherwise unavailable, the Organization requests a written',
      'statement of what was lost, when, and by what process.',
    ].join('\n');
  }
  if (kind === 'prehearing_meeting_demanded') {
    return [
      'The Organization demands a pre-hearing meeting in this matter.',
      '',
      nonEmpty(i.purpose) ? 'Purpose stated by the Organization: ' + String(i.purpose).trim()
                          : 'Purpose: ' + (missing.push('the purpose of the meeting'), NOT_ON_FILE),
    ].join('\n');
  }
  if (kind === 'witnesses_demanded' || kind === 'evidence_demanded') {
    const what = kind === 'witnesses_demanded' ? 'witnesses' : 'evidence';
    const items = Array.isArray(i.items) ? i.items.filter(nonEmpty) : [];
    if (!items.length) missing.push('the ' + what + ' demanded, itemised');
    return [
      'The Organization demands the production of the following ' + what + ' in this matter:',
      '',
      items.length ? items.map((x, n) => '  ' + (n + 1) + '. ' + String(x).trim()).join('\n') : '  ' + NOT_ON_FILE,
    ].join('\n');
  }
  if (kind === 'witness_list_served' || kind === 'exhibits_served') {
    const what = kind === 'witness_list_served' ? 'witnesses' : 'exhibits';
    const items = Array.isArray(i.items) ? i.items.filter(nonEmpty) : [];
    if (!items.length) missing.push('the ' + what + ' listed');
    return [
      'The Organization serves the following ' + what + ' in this matter:',
      '',
      items.length ? items.map((x, n) => '  ' + (n + 1) + '. ' + String(x).trim()).join('\n') : '  ' + NOT_ON_FILE,
    ].join('\n');
  }
  if (kind === 'postponement_requested') {
    // RULING 191: the explanation, and the proof named in the letter where it is enclosed.
    const encl = Array.isArray(i.enclosures) ? i.enclosures.filter(nonEmpty) : [];
    return [
      'The Organization requests postponement of the hearing presently scheduled in this matter'
        + (nonEmpty(k.hearingDate) ? ' for ' + String(k.hearingDate).trim() : '') + '.',
      '',
      nonEmpty(i.reason) ? 'Reason: ' + String(i.reason).trim()
                         : 'Reason: ' + (missing.push('the reason for the postponement'), NOT_ON_FILE),
      ...(encl.length ? ['', 'Enclosed:', ...encl.map((x, n) => '  ' + (n + 1) + '. ' + String(x).trim())] : []),
    ].join('\n');
  }
  if (kind === 'cancellation_requested') {
    const g = CANCELLATION_GROUNDS.find(x => x.key === i.ground);
    const opening = 'The Organization requests that the carrier cancel the hearing presently scheduled in this matter'
      + (nonEmpty(k.hearingDate) ? ' for ' + String(k.hearingDate).trim() : '') + '.';
    if (!g) { missing.push('the ground for the request (' + CANCELLATION_GROUNDS.map(x => x.key).join(', ') + ')'); return [opening, '', 'Ground: ' + NOT_ON_FILE].join('\n'); }
    if (g.key === 'none') return opening;
    if (g.key === 'other') {
      return [opening, '', nonEmpty(i.reason) ? 'Reason: ' + String(i.reason).trim()
                                           : 'Reason: ' + (missing.push('the reason for the request'), NOT_ON_FILE)].join('\n');
    }
    return [opening, '', g.sentence].join('\n');
  }
  missing.push('a body for act "' + kind + '"');
  return NOT_ON_FILE;
}

// ONE letter for ONE act. Returns { ok, letter, missingFields, unresolvedBlocks, sendable }.
// `sendable` is false while any header block or any required field is unresolved - the same guard
// presentmentLetterGroup() uses, and for the same reason: a caller must never mark an unsendable
// letter approved.
export function disciplineLetter(kind, kase, blocks, input, opts) {
  const spec = LETTER_FOR_ACT[kind];
  if (!spec) {
    const named = NOT_GENERATED.find(n => n.act === kind);
    return { ok: false, letter: null, missingFields: [], unresolvedBlocks: [], sendable: false,
      reason: named ? named.why : '"' + kind + '" is not an act that produces a letter from the Organization.' };
  }
  const missing = [];
  const b = blocks || caseHeaderBlocks(kase, {});
  // RULING 188: the same header the claim letters carry, from the one place - the parent International
  // first. This was an inline copy of letterGroup.js's header (§XI); it is now that header. RULING 229:
  // the committee's letterhead banner (opts.letterhead) prints above it when one is set.
  const hdr = letterHeader(b, opts && opts.letterhead);
  const ident = caseIdentity(kase, missing);
  const body = bodyFor(kind, kase, input, missing);
  const unresolvedBlocks = ['generalCommittee', 'localCommittee', 'railroad']
    .filter(k => !b[k].resolved).map(k => b[k].kind);
  const uniq = Array.from(new Set(missing));
  // RULING 231: a produced letter is a DRAFT until the officer signs off on it.
  const text = markDraft(hdr + '\n\nTO: ' + b.railroad.lines.join(', ') + '\n\n' + spec.title + '\n\n' + ident + '\n\n' + body + FOOT);
  return {
    ok: true,
    letter: { kind, title: spec.title, to: 'railroad', text, ruling: spec.ruling },
    warning: riskWarningFor(kind, input),   // ruling 191: shown to the officer before the letter, never in it
    missingFields: uniq,
    unresolvedBlocks,
    sendable: unresolvedBlocks.length === 0 && uniq.length === 0,
  };
}

// Ruling 158: the preservation demand is per media class, so the set for a case is one letter per
// class that is still unissued. Given no classes it produces nothing and says why - it does not
// produce a blank demand.
export function preservationDemandSet(kase, blocks, classes) {
  const want = Array.isArray(classes) ? classes.filter(c => PRESERVATION_CLASSES.includes(c)) : [];
  if (!want.length) {
    return { ok: false, letters: [], reason: 'No media class named. Ruling 158 makes the demand per class ('
      + PRESERVATION_CLASSES.join(', ') + '); a demand naming no class demands nothing.' };
  }
  const letters = want.map(c => {
    const r = disciplineLetter('preservation_demanded', kase, blocks, { mediaClasses: [c] });
    return { mediaClass: c, ...r };
  });
  return { ok: true, letters, sendable: letters.every(l => l.sendable), reason: null };
}

// Everything the Organization can send on a case today, with the reason each unsendable one is
// unsendable. A panel draws this; nothing here decides whether to send.
export function availableLetters(kase, blocks, inputs) {
  const i = inputs || {};
  return LETTER_KINDS.map(kind => {
    const r = disciplineLetter(kind, kase, blocks, i[kind]);
    return { kind, title: LETTER_FOR_ACT[kind].title, ruling: LETTER_FOR_ACT[kind].ruling,
      sendable: r.sendable, missingFields: r.missingFields, unresolvedBlocks: r.unresolvedBlocks };
  });
}

export { NOT_ON_FILE, presentmentLetterGroup };
